import type { OutputOf } from './generated/contracts';

/**
 * **Choosing a category from Shopify's tree** (D59), shared by the phone and the web so the
 * two agree on what a seller is offered. Pure: no transport, no React.
 *
 * `catalog.taxonomy.get` is now about 13,000 categories, so no screen lists them all. A picker
 * offers Going Once's own categories (the ones with a template it sends) until the seller
 * types, then the best matches by name and path. A category's form comes from that response
 * when it carries the template, and from `catalog.category.get` when it does not.
 */

export type Taxonomy = OutputOf<'catalog.taxonomy.get'>;
export type TaxonomyCategory = Taxonomy['categories'][number];
export type CategoryAttribute = Taxonomy['attributes'][number];
export type CategoryEntry = Taxonomy['templates'][number]['entries'][number];

/** What a listing form asks for in one category. */
export interface CategoryForm {
  entries: CategoryEntry[];
  attributes: CategoryAttribute[];
  /** A brand is required to publish in this category (D59). */
  brandRequired: boolean;
}

interface Index {
  byId: Map<string, TaxonomyCategory>;
  configured: Set<string>;
  pickable: TaxonomyCategory[];
}

const indexes = new WeakMap<Taxonomy, Index>();

function indexOf(taxonomy: Taxonomy): Index {
  let index = indexes.get(taxonomy);
  if (!index) {
    index = {
      byId: new Map(taxonomy.categories.map((category) => [category.categoryId, category])),
      configured: new Set(taxonomy.templates.map((template) => template.categoryId)),
      pickable: taxonomy.categories.filter((category) => category.listable && !category.authenticationRequired),
    };
    indexes.set(taxonomy, index);
  }
  return index;
}

/** A category's place in the tree, by name: `Clothing › Dresses` (the top-level group left off). */
export function categoryTrail(taxonomy: Taxonomy, categoryId: string): string {
  const names = ancestry(taxonomy, categoryId);
  return (names.length > 2 ? names.slice(1) : names).join(' › ');
}

/** **The whole path**, top-level group included: what a picker shows so two "Shorts" read apart. */
export function categoryPath(taxonomy: Taxonomy, categoryId: string): string {
  return ancestry(taxonomy, categoryId).join(' › ');
}

function ancestry(taxonomy: Taxonomy, categoryId: string): string[] {
  const { byId } = indexOf(taxonomy);
  const names: string[] = [];
  for (
    let category = byId.get(categoryId);
    category;
    category = category.parentId ? byId.get(category.parentId) : undefined
  ) {
    names.unshift(category.name);
  }
  return names;
}

/** Going Once's own categories: what a picker shows before the seller searches. */
export function suggestedCategories(taxonomy: Taxonomy): TaxonomyCategory[] {
  const { configured, pickable } = indexOf(taxonomy);
  return pickable.filter((category) => configured.has(category.categoryId));
}

const words = (text: string) =>
  text
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((word) => word !== '');

/**
 * **The categories a seller's search means**, best first: every word must appear in the name
 * or the path; a name that starts with the search beats one that contains it, which beats a
 * path that does; then the shallower category, the general shelf before the niche one.
 */
export function searchCategories(taxonomy: Taxonomy, text: string, limit = 20): TaxonomyCategory[] {
  const query = words(text);
  if (query.length === 0) return [];
  const phrase = query.join(' ');
  const scored: { category: TaxonomyCategory; score: number }[] = [];
  for (const category of indexOf(taxonomy).pickable) {
    const name = words(category.name).join(' ');
    const path = words(category.path).join(' ');
    if (!query.every((word) => name.includes(word) || path.includes(word))) continue;
    const rank = name === phrase ? 0 : name.startsWith(phrase) ? 1 : name.includes(phrase) ? 2 : 3;
    // The path is Shopify's full name (`Apparel & Accessories > Clothing > Jeans`): the shallower wins a tie.
    scored.push({ category, score: rank * 100 + category.path.split(' > ').length });
  }
  return scored
    .sort((a, b) => a.score - b.score || a.category.name.localeCompare(b.category.name))
    .slice(0, limit)
    .map((entry) => entry.category);
}

/**
 * **A picker's options**: the search results when there is a search, Going Once's own
 * categories when there is not — and the chosen category always, so a suggestion from the
 * photo stays visible and selected even when it is neither.
 */
export function categoryOptions(
  taxonomy: Taxonomy,
  input: { search?: string; selected?: string | undefined },
): { value: string; label: string }[] {
  const listed = input.search?.trim() ? searchCategories(taxonomy, input.search) : suggestedCategories(taxonomy);
  const selected = input.selected ? indexOf(taxonomy).byId.get(input.selected) : undefined;
  const all = selected && !listed.includes(selected) ? [selected, ...listed] : listed;
  return all.map((category) => ({ value: category.categoryId, label: categoryTrail(taxonomy, category.categoryId) }));
}

/** The form for a category, when `catalog.taxonomy.get` carries it; otherwise ask `catalog.category.get`. */
export function formFromTaxonomy(taxonomy: Taxonomy, categoryId: string): CategoryForm | undefined {
  const template = taxonomy.templates.find((entry) => entry.categoryId === categoryId);
  if (!template) return undefined;
  const keys = new Set(template.entries.map((entry) => entry.key));
  return {
    entries: [...template.entries].sort((a, b) => a.displayOrder - b.displayOrder),
    attributes: inForm(
      taxonomy.attributes.filter((attribute) => keys.has(attribute.key)),
      template.entries,
    ),
    brandRequired: indexOf(taxonomy).byId.get(categoryId)?.brandRequired === true,
  };
}

export function formFromCategory(category: OutputOf<'catalog.category.get'>): CategoryForm {
  return {
    entries: [...category.entries].sort((a, b) => a.displayOrder - b.displayOrder),
    attributes: inForm(category.attributes, category.entries),
    brandRequired: category.category.brandRequired,
  };
}

/**
 * **Each attribute as this category's form takes it** (D59): where the category overrides the
 * input type, a fixed list becomes free text whose values are suggestions (Jeans' Size, which
 * has no 33), or the reverse. Screens read `dataType` and need not know the rule exists. The
 * server's `definitionInForm` is the same rule, and it is what validates.
 */
function inForm(
  attributes: readonly CategoryAttribute[],
  entries: readonly { key: string; inputType?: 'list' | 'free_text' }[],
): CategoryAttribute[] {
  const override = new Map(entries.map((entry) => [entry.key, entry.inputType]));
  return attributes.map((attribute) => {
    const inputType = override.get(attribute.key);
    if (inputType === 'free_text' && attribute.dataType === 'enum') {
      return { ...attribute, dataType: 'string', validation: { maxLength: 80 } };
    }
    if (inputType === 'list' && attribute.dataType === 'string' && (attribute.options?.length ?? 0) > 0) {
      const { validation: _free, ...rest } = attribute;
      return { ...rest, dataType: 'enum' };
    }
    return attribute;
  });
}
